import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, post } from "../../api";
import { Alert, Button, Loading, PageHeader, Panel, PanelHeader, SelectField } from "../../components/ui";
import { useWorkflows } from "../../lookups";
import { type SettingsResponse, useSettings } from "./useSettings";

/** Website lead form: a secret link the website posts to, creating a customer and a job. */
export function LeadsSettings() {
  const qc = useQueryClient();
  const { query, save } = useSettings();
  const workflows = useWorkflows();
  const [copied, setCopied] = useState(false);
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => post<SettingsResponse>("/api/settings/lead-form", { enabled }),
    onSuccess: (d) => qc.setQueryData(["settings"], d),
  });
  if (query.isLoading || !query.data) return <Loading />;
  const url = query.data.leadFormUrl;
  const workflowId = query.data.settings["leads.workflowId"] ?? "";

  const example = url
    ? `<form method="post" action="${url}">
  <input name="name" placeholder="Your name" required>
  <input name="phone" type="tel" placeholder="Phone">
  <input name="email" type="email" placeholder="Email">
  <input name="street" placeholder="Street address">
  <input name="city" placeholder="City">
  <input name="zip" placeholder="ZIP">
  <textarea name="message" placeholder="How can we help?"></textarea>
  <!-- Leave this hidden field empty; it catches spam bots. -->
  <input name="website" style="display:none" tabindex="-1" autocomplete="off">
  <button>Send</button>
</form>`
    : "";

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Website leads" description="The contact form on the NMT website can create a customer and a new job here, with lead source Website." />
      {(toggle.error || save.error) && <Alert>{errorMessage(toggle.error ?? save.error)}</Alert>}
      <Panel>
        <PanelHeader
          title={url ? "On" : "Off"}
          description={url ? "Website form submissions arrive as new jobs in the first stage." : "Turn it on to get a link for the website form."}
          action={
            url ? (
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    if (window.confirm("Make a new link? The website must be updated, because the current link will stop working.")) toggle.mutate(true);
                  }}
                  busy={toggle.isPending}
                >
                  New link
                </Button>
                <Button variant="danger" onClick={() => toggle.mutate(false)} busy={toggle.isPending}>
                  Turn off
                </Button>
              </div>
            ) : (
              <Button variant="primary" onClick={() => toggle.mutate(true)} busy={toggle.isPending}>
                Turn on
              </Button>
            )
          }
        />
        {url && (
          <div className="flex flex-col gap-4 px-5 py-5">
            <div>
              <div className="mb-1 text-sm font-semibold text-ink-soft">Form link</div>
              <div className="flex gap-2">
                <code className="min-w-0 flex-1 truncate rounded bg-mist px-3 py-2 text-sm">{url}</code>
                <Button
                  onClick={async () => {
                    await navigator.clipboard.writeText(url);
                    setCopied(true);
                  }}
                >
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
            <SelectField label="New leads go into" value={workflowId} onChange={(e) => save.mutate({ "leads.workflowId": e.target.value || null })}>
              <option value="">{workflows.data?.[0]?.name ?? "The first workflow"} (first in the list)</option>
              {workflows.data?.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </SelectField>
            <div>
              <div className="mb-1 text-sm font-semibold text-ink-soft">Example form for the website</div>
              <pre className="overflow-x-auto rounded bg-sidebar p-4 text-xs text-white/90">{example}</pre>
              <p className="mt-1 text-sm text-muted">Fields it understands: name (or firstName and lastName), phone, email, street, city, state, zip, message.</p>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
